/**
 * Notifications Page - Trade Loop v2.0
 *
 * Displays user notifications as clickable cards that route to relevant locations
 */

import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import AppLayout from "@/components/layout/AppLayout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import ResendReferralModal from "../components/ResendReferralModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useToast } from "../contexts/ToastContext";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import {
  Bell,
  BellOff,
  Send,
  UserPlus,
  MessageSquare,
  DollarSign,
  CheckCircle,
  XCircle,
  Clock,
  RefreshCw,
  Trash2,
  CheckCheck,
  Filter,
  QrCode,
  ClipboardCheck,
} from "lucide-react";

// Notification types
type NotificationType =
  | "referral_received"
  | "referral_accepted"
  | "referral_declined"
  | "referral_completed"
  | "connection_request"
  | "connection_accepted"
  | "new_message"
  | "payment_received"
  | "payment_sent"
  | "availability_request"
  | "availability_request_extended"
  | "welcome_invite_reminder"
  | "referral_pending_reminder"
  | "referral_close_out_reminder"
  | "referral_sender_unanswered_alert"
  | "connection_request_reminder"
  | "availability_request_reminder"
  | "system";

interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  read: boolean;
  createdAt: string;
  urgencyLevel?: string | null;
  metadata?: {
    referralId?: string;
    partnerId?: string;
    partnerName?: string;
    conversationId?: string;
    amount?: number;
    url?: string;
    // Availability requests carry the request id under both keys — older rows set
    // only `availabilityRequestId`.
    requestId?: string;
    availabilityRequestId?: string;
  };
}

const isUrgentLevel = (level?: string | null): boolean =>
  typeof level === 'string' && level.toLowerCase() === 'urgent';

// Mock data for development
const mockNotifications: Notification[] = [
  {
    id: "1",
    type: "referral_received",
    title: "New Referral Received",
    message: "John Smith sent you a referral for a kitchen remodel job",
    read: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 mins ago
    metadata: { referralId: "ref-123", partnerId: "p-1", partnerName: "John Smith" },
  },
  {
    id: "2",
    type: "connection_request",
    title: "New Connection Request",
    message: "Sarah Johnson wants to connect with you",
    read: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(), // 2 hours ago
    metadata: { partnerId: "p-2", partnerName: "Sarah Johnson" },
  },
  {
    id: "3",
    type: "referral_accepted",
    title: "Referral Accepted",
    message: "Mike's Plumbing accepted your referral",
    read: false,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(), // 5 hours ago
    metadata: { referralId: "ref-456", partnerId: "p-3", partnerName: "Mike's Plumbing" },
  },
  {
    id: "4",
    type: "referral_completed",
    title: "Referral Completed",
    message: "Your referral to ABC Electric has been marked as completed",
    read: true,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(), // 1 day ago
    metadata: { referralId: "ref-789", partnerName: "ABC Electric" },
  },
  {
    id: "5",
    type: "new_message",
    title: "New Message",
    message: "David from Dave's HVAC sent you a message",
    read: true,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(), // 2 days ago
    metadata: { conversationId: "conv-123", partnerId: "p-4", partnerName: "Dave's HVAC" },
  },
  {
    id: "6",
    type: "referral_completed",
    title: "Referral Completed",
    message: "The referral to Premium Roofing has been marked as completed",
    read: true,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(), // 3 days ago
    metadata: { referralId: "ref-101", partnerName: "Premium Roofing" },
  },
  {
    id: "7",
    type: "connection_accepted",
    title: "Connection Accepted",
    message: "Elite Landscaping accepted your connection request",
    read: true,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 4).toISOString(), // 4 days ago
    metadata: { partnerId: "p-5", partnerName: "Elite Landscaping" },
  },
  {
    id: "8",
    type: "referral_declined",
    title: "Referral Declined",
    message: "Quick Fix Repairs declined your referral",
    read: true,
    createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(), // 5 days ago
    metadata: { referralId: "ref-202", partnerName: "Quick Fix Repairs" },
  },
];

const NotificationsPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();

  const [filterType, setFilterType] = useState<string>("all");
  const [filterRead, setFilterRead] = useState<string>("all");
  const [resendReferralId, setResendReferralId] = useState<string | null>(null);

  // Fetch the declined referral on demand (only when the resend modal is opened).
  const { data: resendReferral, isLoading: isLoadingResendReferral } = useQuery({
    queryKey: [`/api/workflows/referral/${resendReferralId}`, "resend"],
    queryFn: async () => {
      const response = await api.get(`/api/workflows/referral/${resendReferralId}`);
      return (response as any)?.data || response;
    },
    enabled: !!resendReferralId,
  });

  // Fetch notifications
  const { data: notificationsData, isPending, error } = useQuery({
    queryKey: ["notifications", user?.id],
    queryFn: async () => {
      const response = await api.get("/api/workflows/notifications");
      return response.data || [];
    },
    enabled: !!user,
    refetchOnMount: 'always', // Always refetch when navigating to this page
    refetchOnWindowFocus: true, // Refetch when user returns to the tab
    staleTime: 0, // Always consider data stale
  });

  const notifications = notificationsData || [];

  // Mark notification as read
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      console.log('[NotificationsPage] Sending mark-as-read request for:', notificationId);
      const response = await api.patch(`/api/workflows/notifications/${notificationId}/read`);
      console.log('[NotificationsPage] Mark-as-read response:', response);
      return response;
    },
    onMutate: async (notificationId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["notifications", user?.id] });

      // Snapshot the previous value
      const previousNotifications = queryClient.getQueryData(["notifications", user?.id]);

      // Optimistically update to the new value
      queryClient.setQueryData(["notifications", user?.id], (old: any) => {
        if (!Array.isArray(old)) return old;
        return old.map((notification: any) =>
          notification.id === notificationId
            ? { ...notification, read: true }
            : notification
        );
      });

      // Return context with the previous value
      return { previousNotifications };
    },
    onSuccess: (data, notificationId) => {
      console.log('[NotificationsPage] Successfully marked as read:', notificationId);
      // Invalidate to refetch with server data
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["unreadNotifications"] });
    },
    onError: (error, notificationId, context) => {
      console.error('[NotificationsPage] Failed to mark as read:', { notificationId, error });
      // Rollback to previous value on error
      if (context?.previousNotifications) {
        queryClient.setQueryData(["notifications", user?.id], context.previousNotifications);
      }
      showError("Error", "Failed to mark notification as read");
    },
  });

  // Mark all as read
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      return api.patch("/api/workflows/notifications/mark-all-read");
    },
    onSuccess: () => {
      // Invalidate both the notifications list AND the unread count for the badge
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["unreadNotifications"] });
      showSuccess("All Caught Up", "All notifications marked as read");
    },
    onError: () => {
      showError("Error", "Failed to mark notifications as read");
    },
  });

  // Delete notification
  const deleteMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      return api.delete(`/api/workflows/notifications/${notificationId}`);
    },
    onSuccess: () => {
      // Invalidate both the notifications list AND the unread count for the badge
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["unreadNotifications"] });
      showSuccess("Deleted", "Notification removed");
    },
    onError: () => {
      showError("Error", "Failed to delete notification");
    },
  });

  // Get icon for notification type
  const getNotificationIcon = (type: NotificationType) => {
    switch (type) {
      case "referral_received":
        return <Send className="h-5 w-5 text-blue-500" />;
      case "referral_accepted":
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case "referral_declined":
        return <XCircle className="h-5 w-5 text-red-500" />;
      case "referral_completed":
        return <CheckCheck className="h-5 w-5 text-green-600" />;
      case "connection_request":
        return <UserPlus className="h-5 w-5 text-purple-500" />;
      case "connection_accepted":
        return <UserPlus className="h-5 w-5 text-green-500" />;
      case "new_message":
        return <MessageSquare className="h-5 w-5 text-blue-500" />;
      case "payment_received":
        return <DollarSign className="h-5 w-5 text-green-500" />;
      case "payment_sent":
        return <DollarSign className="h-5 w-5 text-orange-500" />;
      case "availability_request":
        return <Clock className="h-5 w-5 text-amber-500" />;
      case "availability_request_extended":
        return <RefreshCw className="h-5 w-5 text-amber-500" />;
      case "welcome_invite_reminder":
        return <QrCode className="h-5 w-5 text-purple-500" />;
      case "referral_pending_reminder":
        return <Clock className="h-5 w-5 text-blue-500" />;
      case "referral_close_out_reminder":
        return <ClipboardCheck className="h-5 w-5 text-green-600" />;
      case "referral_sender_unanswered_alert":
        return <Clock className="h-5 w-5 text-orange-500" />;
      case "connection_request_reminder":
        return <UserPlus className="h-5 w-5 text-purple-500" />;
      case "availability_request_reminder":
        return <Clock className="h-5 w-5 text-amber-500" />;
      case "system":
      default:
        return <Bell className="h-5 w-5 text-gray-500" />;
    }
  };

  // Get route for notification type
  const getNotificationRoute = (notification: Notification): string | null => {
    // Referral notifications deep-link to the referral itself. This takes priority
    // over metadata.url, which every referral notification sets to the bare
    // "/referrals" list — where the referral is unreachable once the user has
    // hidden it from their view.
    if (notification.type.startsWith("referral") && notification.metadata?.referralId) {
      return `/referrals?id=${notification.metadata.referralId}`;
    }

    // Availability notifications deep-link to the request itself. This is resolved
    // from the id rather than metadata.url, because rows created before the
    // /availability-requests/:id route existed still carry a bare "/dashboard" or
    // "/referrals" url — which is exactly the "lands on a page, go find it yourself"
    // bug. Deriving from the id repairs those old rows too.
    if (notification.type.startsWith("availability")) {
      const requestId =
        notification.metadata?.requestId || notification.metadata?.availabilityRequestId;
      if (requestId) {
        return `/availability-requests/${requestId}`;
      }
    }

    // Check if notification has a custom URL in metadata
    if (notification.metadata?.url) {
      return notification.metadata.url;
    }

    switch (notification.type) {
      // The referral reminders and the sender's "no response yet" alert normally deep-link
      // via metadata.referralId above.
      case "referral_received":
      case "referral_accepted":
      case "referral_declined":
      case "referral_completed":
      case "referral_pending_reminder":
      case "referral_close_out_reminder":
      case "referral_sender_unanswered_alert":
        return "/referrals";
      case "connection_request":
        // Connection requests now go to messages page
        return "/messages";
      case "connection_request_reminder":
        // Pending partner request nudge: Network lists incoming requests with Accept / Decline.
        return "/network";
      case "connection_accepted":
        return notification.metadata?.partnerId
          ? `/network?partner=${notification.metadata.partnerId}`
          : "/network";
      case "new_message":
        return notification.metadata?.conversationId
          ? `/messages?conversation=${notification.metadata.conversationId}`
          : "/messages";
      case "payment_received":
      case "payment_sent":
        return "/referrals?tab=completed";
      case "availability_request":
        // Navigate to dashboard which will open the response modal
        return "/dashboard";
      case "availability_request_extended":
        // Reopened availability request — drop the user on the Referrals page
        // with the Availability Requests panel pre-expanded.
        return "/referrals?section=availability-requests";
      case "welcome_invite_reminder":
        // Invite-your-partners nudge opens the QR Connect / invitation screen.
        return "/qr";
      case "system":
      default:
        return null;
    }
  };

  // Handle notification click
  const handleNotificationClick = async (notification: Notification) => {
    console.log('[NotificationsPage] Notification clicked:', {
      id: notification.id,
      type: notification.type,
      read: notification.read,
      metadata: notification.metadata
    });

    // Mark as read
    if (!notification.read) {
      console.log('[NotificationsPage] Marking notification as read:', notification.id);
      markAsReadMutation.mutate(notification.id);
    } else {
      console.log('[NotificationsPage] Notification already read');
    }

    // For declined referrals, open the Resend dialog inline so the sender can
    // forward the same client/job to another partner without navigating away.
    if (notification.type === 'referral_declined' && notification.metadata?.referralId) {
      setResendReferralId(String(notification.metadata.referralId));
      return;
    }

    // Navigate to relevant page
    const route = getNotificationRoute(notification);
    console.log('[NotificationsPage] Routing to:', route);

    if (route) {
      const navState: any = {};

      // For connection requests and messages, invalidate messages query to ensure fresh data
      if (notification.type === 'connection_request' || notification.type === 'new_message') {
        console.log('[NotificationsPage] Invalidating messages queries before navigation');
        await queryClient.invalidateQueries({ queryKey: ["messages", user?.id] });
        await queryClient.invalidateQueries({ queryKey: ["unreadMessages"] });

        // Set specific flags based on notification type
        if (notification.type === 'connection_request') {
          navState.isConnectionRequest = true;
        }

        // Add partnerId if available (new notifications will have this)
        if (notification.metadata?.partnerId) {
          navState.autoSelectPartnerId = notification.metadata.partnerId;
        }

        console.log('[NotificationsPage] Navigating with state:', navState);
        navigate(route, { state: navState });
      } else {
        navigate(route);
      }
    }
  };

  // Format relative time
  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  // Filter notifications
  const filteredNotifications = useMemo(() => {
    return notifications.filter((n) => {
      // Filter by type
      if (filterType !== "all") {
        if (filterType === "referrals" && !n.type.startsWith("referral")) return false;
        if (filterType === "connections" && !n.type.startsWith("connection")) return false;
        if (filterType === "messages" && n.type !== "new_message") return false;
        if (filterType === "payments" && !n.type.startsWith("payment")) return false;
      }

      // Filter by read status
      if (filterRead === "unread" && n.read) return false;
      if (filterRead === "read" && !n.read) return false;

      return true;
    });
  }, [notifications, filterType, filterRead]);

  // Count unread
  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <AppLayout>
      <div className="container mx-auto px-4 py-6 max-w-3xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Bell className="h-8 w-8 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">Notifications</h1>
              {unreadCount > 0 && (
                <p className="text-sm text-muted-foreground">
                  {unreadCount} unread notification{unreadCount !== 1 ? "s" : ""}
                </p>
              )}
            </div>
          </div>
          {unreadCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => markAllAsReadMutation.mutate()}
              disabled={markAllAsReadMutation.isPending}
            >
              <CheckCheck className="h-4 w-4 mr-2" />
              Mark All Read
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="referrals">Referrals</SelectItem>
                <SelectItem value="connections">Connections</SelectItem>
                <SelectItem value="messages">Messages</SelectItem>
                <SelectItem value="payments">Payments</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Select value={filterRead} onValueChange={setFilterRead}>
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="All" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="unread">Unread</SelectItem>
              <SelectItem value="read">Read</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Notifications List */}
        {isPending ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <XCircle className="h-12 w-12 text-red-500 mb-4" />
              <h3 className="text-lg font-medium mb-1">Error Loading Notifications</h3>
              <p className="text-sm text-muted-foreground text-center">
                {error instanceof Error ? error.message : 'Failed to load notifications'}
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] })}
              >
                Try Again
              </Button>
            </CardContent>
          </Card>
        ) : filteredNotifications.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <BellOff className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-1">No Notifications</h3>
              <p className="text-sm text-muted-foreground text-center">
                {filterType !== "all" || filterRead !== "all"
                  ? "No notifications match your filters"
                  : "You're all caught up!"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredNotifications.map((notification) => {
              const urgent = isUrgentLevel(notification.urgencyLevel);
              return (
              <Card
                key={notification.id}
                className={`cursor-pointer transition-all hover:shadow-md ${
                  urgent
                    ? "border-l-4 border-l-red-600 bg-red-50 border-red-300"
                    : !notification.read
                      ? "border-l-4 border-l-primary bg-primary/5"
                      : ""
                }`}
                onClick={() => handleNotificationClick(notification)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className="flex-shrink-0 mt-1">
                      {getNotificationIcon(notification.type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <h3
                            className={`font-medium ${
                              urgent
                                ? "text-red-900"
                                : !notification.read
                                  ? "text-foreground"
                                  : "text-muted-foreground"
                            }`}
                          >
                            {notification.title}
                          </h3>
                          <p
                            className={`text-sm mt-0.5 ${
                              urgent ? "text-red-800" : "text-muted-foreground"
                            }`}
                          >
                            {notification.message}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {urgent && (
                            <Badge
                              className="bg-red-600 hover:bg-red-700 text-white border-0 text-xs uppercase tracking-wide"
                              aria-label="Urgent referral"
                            >
                              Urgent
                            </Badge>
                          )}
                          {!notification.read && (
                            <Badge variant="default" className="text-xs">
                              New
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Footer */}
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {formatRelativeTime(notification.createdAt)}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteMutation.mutate(notification.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Resend Referral Modal — opened when a referral_declined notification is clicked */}
      {resendReferralId && (
        <>
          {isLoadingResendReferral ? (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg p-6">
                <p className="text-gray-600">Loading referral details...</p>
              </div>
            </div>
          ) : resendReferral ? (
            <ResendReferralModal
              referral={resendReferral}
              isOpen={!!resendReferralId}
              onClose={() => setResendReferralId(null)}
            />
          ) : null}
        </>
      )}
    </AppLayout>
  );
};

export default NotificationsPage;
